/* eslint no-param-reassign: "off", no-console: "off" */
import Vuex from 'vuex';

const deviceStore = {
  state: {
    devices: [],
    count: 0,
    devicesLoading: false,
    device: null,
    deviceLoading: false,
    deviceRefreshing: false,
    models: [],
    types: [],
    features: [],
    versions: [],
    filteredVersionsByDevice: [],
    tableFilterParam: null
  },
  mutations: {
    fetchDevices(state, payload) {
      state.devices = payload.payload ?? [];
      state.count = payload.count ?? 0;
    },
    fetchDevice(state, payload) {
      state.device = payload;
    },
    addDevice(state, payload) {
      state.device = payload;
    },
    setDeviceConfigs(state, payload) {
      state.models = payload.models;
      state.types = payload.types;
      state.features = payload.features;
    },
    setFeatureCertificate(state, payload) {
      state.device.features = payload.features ? payload.features.split(',') : [];
      state.device.featuresPublicKey = payload.publicKey;
    },
    fetchDeviceSoftwareVersions(state, payload) {
      state.versions = payload;
    },
    fetchDeviceSoftwareVersionsBySerial(state, payload) {
      state.filteredVersionsByDevice = payload;
    },
    tableFilterParam(state, payload) {
      state.tableFilterParam = payload;
    },
    fetchDeviceGroups(state, payload) {
      if (state.device) state.device.groups = payload;
    },
    updateDevice(state, payload) {
      // merge the old with the new instead of atomic overwrite
      state.device = {
        ...state.device,
        ...payload.device
      };

      const idx = state.devices.findIndex(r => r.id == payload.device.id);
      if (idx == -1) return;

      // if the updated device changed accounts, check to see that the user has permission to the account
      const userAccountMatch = payload.rootState.accounts.accounts.find(a => a.id == payload.device.accountId);
      if (!userAccountMatch) { 
        // user does not have permission to the new account, remove the device from the list
        state.devices.splice(idx, 1);
      }

      state.devices = JSON.parse(JSON.stringify(state.devices));
    },
    cancelPendingDeviceTransfer(state) {
      if (state.device) state.device.pendingTransferEmail = null;
    }
  },
  actions: {
    /**
     * Fetches a list of devices
     * @param {*} param0 
     * @param {Object} payload - Object containing paging, sorting, filters
     */
    async fetchDevices({ state, commit }, payload) {
      try {
        if (!(payload.bypassState)) state.devicesLoading = true;
        if (!(payload.bypassState)) commit('tableFilterParam', payload);
        const res = await fetch(`/v2/devices?from=${payload.from}&take=${payload.take}&select=${payload.select || ''}&filters=${payload.filters || ''}&sortBy=${payload.sortBy || ''}&sortDirection=${payload.sortDirection || ''}`);
        if (!res.ok) throw new Error(await res.text());
        if (!(payload.bypassState)) state.devicesLoading = false;
        const data = await res.json();
        if (!(payload.bypassState)) commit('fetchDevices', data);
        return data;
      } catch (ex) {
        return { isError: true, ex };
      }
    },
    /**
     * Fetches an individual remote
     * @param {*} param0 
     * @param {string} payload - Individual remote Id
     */
    async fetchDevice({ state, commit }, payload) {
      state.deviceLoading = true;
      // Try to fetch the remote from idxdb first
      try {
        // eslint-disable-next-line no-undef
        const result = await $deviceStore.getItem(payload);
        // If a remote is found in idxdb
        if (result != null) {
          // Commit it so the page loads
          commit('fetchDevice', result);
          // Stop the loading
          state.deviceLoading = false;
          // Start the refresh
          state.deviceRefreshing = true;
        }
      } catch (ex) {
        // If there's an error, failover to API
        console.error(ex);
      }

      try {
        const res = await fetch(`/v2/devices/${payload}`);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        commit('fetchDevice', data);
        state.deviceLoading = false;
        state.deviceRefreshing = false;
        return true;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async fetchDeviceConfigs({ commit }) {
      // retrieve from storage first
      try {
        // eslint-disable-next-line no-undef
        const features = await $deviceConfigStore.getItem('features');
        // eslint-disable-next-line no-undef
        const models = await $deviceConfigStore.getItem('models');
        // eslint-disable-next-line no-undef
        const types = await $deviceConfigStore.getItem('types');

        commit('setDeviceConfigs', { 
          features: features || [],
          models: models || [], 
          types: types || []
        });
      } catch (ex) { 
        console.error(ex); 
      }

      // then, fetch from DB
      try {
        const featuresRes = await fetch('/v2/info/antennaFeatures');
        const modelsRes = await fetch('/v2/info/deviceModels');
        const typesRes = await fetch('/v2/info/deviceTypes');
        
        // check for failed requests
        if (!featuresRes.ok) throw new Error('Unable to fetch Antenna Features.');
        if (!modelsRes.ok) throw new Error('Unable to fetch Device Models.');
        if (!typesRes.ok) throw new Error('Unable to fetch Device Types.');

        // acquire JSON result from responses
        const features = await featuresRes.json();
        const models = await modelsRes.json();
        const types = await typesRes.json();

        // set vuex state
        commit('setDeviceConfigs', { 
          features,
          models, 
          types
        });

        // update cached values
        // eslint-disable-next-line no-undef
        $deviceConfigStore.setItem('features', features);
        // eslint-disable-next-line no-undef
        $deviceConfigStore.setItem('models', models);
        // eslint-disable-next-line no-undef
        $deviceConfigStore.setItem('types', types);

        return true;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async fetchDeviceSoftwareVersions({ commit }) {
      try {
        // eslint-disable-next-line no-undef
        const versions = $deviceConfigStore.getItem('softwareVersions');
        if (versions && versions.length) { commit('fetchDeviceSoftwareVersions', versions); }
      } catch (ex) { console.error(ex); }

      try {
        const res = await fetch('/v3/info/softwareVersions');
        if (!res.ok) throw new Error(await res.text());
        const versionz = await res.json();
        commit('fetchDeviceSoftwareVersions', versionz);
        // eslint-disable-next-line no-undef
        $deviceConfigStore.setItem('softwareVersions', versionz);
        return true;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async fetchDeviceSoftwareVersionsBySerial({ commit }, payload) {
      try {
        // Get filtered software version based on device serial and current version
        const filterVersions = await fetch(`/v3/info/softwareVersions?deviceSerial=${payload.serial}&currentversion=${payload.currentVersion}`);
        if (filterVersions.ok) {
          const filterVersionsData = await filterVersions.json();
          commit('fetchDeviceSoftwareVersionsBySerial', filterVersionsData);
        } else {
          console.error(filterVersions);
        }
      } catch (ex) {
        console.error(ex);
      }
    },
    async generateDeviceFeaturesCertificate({ commit }, payload) {
      try {
        const res = await fetch(`/v2/devices/${payload.device.id}/features`, {
          body: JSON.stringify(payload.features),
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        if (!res.ok) throw new Error(await res.text());

        // extract data from response
        const data = await res.json();
        if (!data) return false;

        // commit to update device with certificate data
        commit('setFeatureCertificate', data);

        return data;
      } catch (err) {
        console.error(err);
        return false;
      }
    },
    async executeDeviceCommand({ dispatch }, payload) {
      try {
        const res = await fetch(`/v2/devices/${payload.id}/command/${payload.cmd}`, {
          body: JSON.stringify(payload),
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        if (!res.ok) throw new Error(await res.text())
        await dispatch('fetchDevice', payload.id);
        return true;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async addDevice({ commit, dispatch }, payload) {
      try {
        const res = await fetch('/v2/devices', {
          body: JSON.stringify(payload),
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        if (!res.ok) return { success: false, message: await res.text() };
        commit('addDevice', await res.json());
        await dispatch('fetchDevices', {});
        return { success: true };
      } catch (ex) {
        return { success: false, message: ex };
      }
    },
    async transferDevice({ commit, rootState }, payload) {
      try {
        const res = await fetch(`/v2/devices/${payload.id}/transfer`, {
          body: JSON.stringify(payload),
          method: 'PUT',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        if (!res.ok) throw new Error(await res.text());
        const device = await res.json();
        commit('updateDevice', { rootState, device });
        return { isSuccess: true, device };
      } catch (ex) {
        console.error(ex);
        return { isSuccess: false, error: ex.message };
      }
    },
    async cancelPendingDeviceTransfer({ commit, rootState }, payload) {
      try {
        const res = await fetch(`/v2/devices/${payload.id}/transfer`, {
          method: 'DELETE',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        if (!res.ok) throw new Error(await res.text());
        const device = await res.json(); 
        commit('updateDevice', { rootState, device });
        commit('cancelPendingDeviceTransfer', {});
        return { isSuccess: true, updatedDevice: device };
      } catch (ex) {
        console.error(ex);
        return { isSuccess: false, error: ex.message };
      }
    },
    async clearDeviceCache() {
      // eslint-disable-next-line no-undef
      await $deviceStore.clear();
      return true;
    },
    async clearModelCache() {
      // eslint-disable-next-line no-undef
      await $deviceConfigStore.clear();
      return true;
    },
    async fetchDeviceGroups({ commit }, payload) {
      try {
        const res = await fetch(`/v2/groups/entities/${payload.entityId}?entityType=${payload.entityType}`);
        if (!res.ok) throw new Error(await res.text());
        const groupsData = await res.json();
        commit('fetchDeviceGroups', groupsData);
        return groupsData;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    }
  },
  getters: {

  }
}
export default deviceStore;
export const useDevicesStore = () => new Vuex.Store(deviceStore);
